import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import {
    PasswordHistoryCheckRequest,
    PasswordHistoryCheckResponse,
    PasswordPolicy,
    PasswordValidationRequest,
    PasswordValidationResult,
} from '../interfaces/password-policy';
import { UsersService } from './users.service';

@Injectable({
    providedIn: 'root',
})
export class PasswordPolicyService {
    constructor(private readonly userService: UsersService) {}

    async getPasswordPolicy(): Promise<PasswordPolicy> {
        if (environment.passwordPolicy.useMockApi) {
            return this.normalizePolicy(environment.passwordPolicy);
        }

        const response: any = await this.userService.getPasswordPolicy();
        return this.normalizePolicy(response?.result?.[0] ?? response?.result ?? response);
    }

    async checkPasswordHistory(
        request: PasswordHistoryCheckRequest
    ): Promise<PasswordHistoryCheckResponse> {
        if (environment.passwordPolicy.useMockApi) {
            return {
                isReused: false,
                previousPasswordsChecked: environment.passwordPolicy.preventReuseCount,
            };
        }

        const response: any = await this.userService.checkPasswordHistory(request);
        return this.normalizeHistoryResponse(
            response?.result?.[0] ?? response?.result ?? response,
            request.password
        );
    }

    async validatePassword(
        request: PasswordValidationRequest
    ): Promise<PasswordValidationResult> {
        const policy = await this.getPasswordPolicy();
        const password = String(request.newPassword || '').trim();
        const confirmPassword = String(request.confirmPassword || '').trim();

        if (!password) {
            return { valid: false, message: 'Password is Required', policy };
        }

        if (!confirmPassword) {
            return { valid: false, message: 'Confirm Password is Required', policy };
        }

        if (password !== confirmPassword) {
            return {
                valid: false,
                message: 'Please confrim your password correctly',
                policy,
            };
        }

        if (
            request.scope === 'change-password' &&
            request.currentPassword &&
            request.currentPassword === password
        ) {
            return {
                valid: false,
                message: 'New password must be different from old password',
                policy,
            };
        }

        const ruleViolation = this.getRuleViolationMessage(password, policy);
        if (ruleViolation) {
            return { valid: false, message: ruleViolation, policy };
        }

        let historyResponse: PasswordHistoryCheckResponse | null = null;

        if (policy.enableHistoryCheck) {
            historyResponse = await this.checkPasswordHistory({
                email: request.email,
                password,
            });
        }

        if (historyResponse?.isReused) {
            return {
                valid: false,
                message: `You cannot reuse any of your last ${policy.preventReuseCount} saved passwords.`,
                policy,
            };
        }

        return {
            valid: true,
            policy,
            passwordExpiresAt: policy.enableExpirationCheck
                ? historyResponse?.passwordExpiresAt ?? this.buildPasswordExpiry(policy)
                : undefined,
        };
    }

    getPolicySummary(policy: PasswordPolicy | null | undefined): string {
        if (!policy) {
            return '';
        }

        const requirements: string[] = [
            `${policy.minLength}-${policy.maxLength} characters`,
        ];

        if (policy.requireUppercase) {
            requirements.push('1 uppercase');
        }
        if (policy.requireLowercase) {
            requirements.push('1 lowercase');
        }
        if (policy.requireNumber) {
            requirements.push('1 number');
        }
        if (policy.requireSpecialCharacter) {
            requirements.push('1 special character');
        }

        if (policy.enableExpirationCheck) {
            requirements.push(`expires in ${policy.expireAfterDays} days`);
        }
        if (policy.enableHistoryCheck) {
            requirements.push(`cannot match last ${policy.preventReuseCount} passwords`);
        }

        return `Password must contain ${requirements.join(', ')}.`;
    }

    private getRuleViolationMessage(password: string, policy: PasswordPolicy): string | null {
        if (password.length < policy.minLength || password.length > policy.maxLength) {
            return `Password must be ${policy.minLength}-${policy.maxLength} characters long`;
        }

        if (policy.requireUppercase && !/[A-Z]/.test(password)) {
            return 'Password must include at least 1 uppercase letter';
        }

        if (policy.requireLowercase && !/[a-z]/.test(password)) {
            return 'Password must include at least 1 lowercase letter';
        }

        if (policy.requireNumber && !/[0-9]/.test(password)) {
            return 'Password must include at least 1 number';
        }

        if (
            policy.requireSpecialCharacter &&
            !new RegExp(policy.specialCharacterPattern).test(password)
        ) {
            return 'Password must include at least 1 special character';
        }

        return null;
    }

    private normalizePolicy(policy: any): PasswordPolicy {
        const fallback = environment.passwordPolicy;

        return {
            minLength: Number(policy?.minLength ?? fallback.minLength),
            maxLength: Number(policy?.maxLength ?? fallback.maxLength),
            requireUppercase: Boolean(policy?.requireUppercase ?? fallback.requireUppercase),
            requireLowercase: Boolean(policy?.requireLowercase ?? fallback.requireLowercase),
            requireNumber: Boolean(policy?.requireNumber ?? fallback.requireNumber),
            requireSpecialCharacter: Boolean(
                policy?.requireSpecialCharacter ?? fallback.requireSpecialCharacter
            ),
            specialCharacterPattern: String(
                policy?.specialCharacterPattern ?? fallback.specialCharacterPattern
            ),
            enableHistoryCheck: Boolean(
                policy?.enableHistoryCheck ?? fallback.enableHistoryCheck
            ),
            preventReuseCount: Number(policy?.preventReuseCount ?? fallback.preventReuseCount),
            enableExpirationCheck: Boolean(
                policy?.enableExpirationCheck ?? fallback.enableExpirationCheck
            ),
            expireAfterDays: Number(policy?.expireAfterDays ?? fallback.expireAfterDays),
        };
    }

    private normalizeHistoryResponse(
        response: any,
        password: string
    ): PasswordHistoryCheckResponse {
        return {
            isReused: Boolean(response?.isReused),
            previousPasswordsChecked: Number(
                response?.previousPasswordsChecked ?? environment.passwordPolicy.preventReuseCount
            ),
            passwordLastChangedAt: response?.passwordLastChangedAt,
            passwordExpiresAt: response?.passwordExpiresAt,
        };
    }

    private buildPasswordExpiry(policy: PasswordPolicy, lastChangedAt = new Date()): string {
        const expiresAt = new Date(lastChangedAt);
        expiresAt.setDate(expiresAt.getDate() + policy.expireAfterDays);
        return expiresAt.toISOString();
    }
}
